import type { PricelistGridCellDto, PricelistGridDto } from "../api/schemas";
import {
  calculateRateFromDiscount,
  normalizeNonNegativeNumber,
  normalizePercent
} from "../domain/pricing";
import {
  makeCartItemId,
  type CartItem,
  type DocumentType,
  type WorkspaceState
} from "../domain/quotation";

export const initialWorkspaceState: WorkspaceState = {
  showPriceBreakdown: false,
  documentType: "quotation",
  selectedBrand: "",
  selectedCategory: "",
  selectedPricelistTag: "",
  globalDiscount: 0,
  globalMarkup: 0,
  deliveryCharges: 0,
  customerLines: "",
  termsText: [
    "Above price is Ex-Godown Kolkata",
    "",
    "Make: KEI",
    "Qty. Tolerance: +/-5%",
    "Delivery: Ready stock subject to prior sale",
    "Payment: Full payment against proforma invoice",
    "Validity: 2 days from today i.e. upto "
  ].join("\n"),
  cart: [],
  comparison: {
    brand: "",
    rows: []
  }
};

export type WorkspaceAction =
  | { type: "replace-state"; state: WorkspaceState }
  | { type: "set-price-breakdown"; enabled: boolean }
  | { type: "set-document-type"; documentType: DocumentType }
  | { type: "select-brand"; brand: string }
  | { type: "select-category"; category: string }
  | { type: "select-pricelist"; tag: string }
  | { type: "set-global-discount"; discount: number | undefined }
  | { type: "set-global-markup"; markup: number | undefined }
  | { type: "set-delivery-charges"; deliveryCharges: number | undefined }
  | { type: "set-customer-lines"; customerLines: string }
  | { type: "set-terms-text"; termsText: string }
  | { type: "add-cell"; grid: PricelistGridDto; cell: PricelistGridCellDto }
  | { type: "remove-item"; id: string }
  | { type: "update-quantity"; id: string; quantity: number | undefined }
  | { type: "update-discount"; id: string; discount: number | undefined }
  | { type: "update-rate"; id: string; rate: number | undefined }
  | { type: "update-markup"; id: string; markup: number | undefined }
  | { type: "toggle-frls"; id: string; enabled: boolean }
  | { type: "toggle-pvc"; id: string; enabled: boolean }
  | { type: "set-comparison-brand"; brand: string }
  | { type: "sync-comparison-items"; itemIds: string[] }
  | { type: "update-comparison-discount"; id: string; discount: number | undefined }
  | { type: "update-comparison-rate"; id: string; rate: number | undefined }
  | {
      type: "set-comparison-list-price";
      id: string;
      brand: string;
      listPrice: number | undefined;
      warning: string | undefined;
    }
  | {
      type: "sync-selection";
      brand: string;
      category: string;
      tag: string;
    };

export function workspaceReducer(
  state: WorkspaceState,
  action: WorkspaceAction
): WorkspaceState {
  switch (action.type) {
    case "replace-state":
      return action.state;
    case "set-price-breakdown":
      return { ...state, showPriceBreakdown: action.enabled };
    case "set-document-type":
      return { ...state, documentType: action.documentType };
    case "select-brand":
      return {
        ...state,
        selectedBrand: action.brand,
        selectedCategory: "",
        selectedPricelistTag: ""
      };
    case "select-category":
      return { ...state, selectedCategory: action.category, selectedPricelistTag: "" };
    case "select-pricelist":
      return { ...state, selectedPricelistTag: action.tag };
    case "set-global-discount": {
      const globalDiscount =
        action.discount === undefined ? undefined : normalizePercent(action.discount);
      return {
        ...state,
        globalDiscount,
        cart: state.cart.map((item) =>
          followsGlobalPricing(item)
            ? { ...item, pricingMode: "global", discount: globalDiscount, rate: rateForItem(item, globalDiscount ?? 0) }
            : item
        )
      };
    }
    case "set-global-markup": {
      const globalMarkup =
        action.markup === undefined ? undefined : normalizePercent(action.markup);
      return {
        ...state,
        globalMarkup,
        cart: state.cart.map((item) =>
          followsGlobalPricing(item) ? { ...item, markup: globalMarkup } : item
        )
      };
    }
    case "set-delivery-charges":
      return {
        ...state,
        deliveryCharges:
          action.deliveryCharges === undefined
            ? undefined
            : normalizeNonNegativeNumber(action.deliveryCharges)
      };
    case "set-customer-lines":
      return { ...state, customerLines: action.customerLines };
    case "set-terms-text":
      return { ...state, termsText: action.termsText };
    case "add-cell":
      return addCellToCart(state, action.grid, action.cell);
    case "remove-item":
      return {
        ...state,
        cart: state.cart.filter((item) => item.id !== action.id),
        comparison: {
          ...state.comparison,
          rows: state.comparison.rows.filter((row) => row.cartItemId !== action.id)
        }
      };
    case "update-quantity":
      return updateCartItem(state, action.id, {
        quantity:
          action.quantity === undefined
            ? undefined
            : Math.max(1, Math.round(action.quantity || 1))
      });
    case "update-discount":
      return updateCartItemWithReprice(state, action.id, (item) => {
        const discount =
          action.discount === undefined ? undefined : normalizePercent(action.discount);
        return {
          ...item,
          pricingMode: "row",
          discount,
          rate: rateForItem(item, discount ?? 0)
        };
      });
    case "update-rate":
      return updateCartItem(state, action.id, {
        pricingMode: "manual",
        rate:
          action.rate === undefined ? undefined : normalizeNonNegativeNumber(action.rate)
      });
    case "update-markup":
      return updateCartItem(state, action.id, {
        markup: action.markup === undefined ? undefined : normalizePercent(action.markup)
      });
    case "toggle-frls":
      return updateCartItemWithReprice(state, action.id, (item) => {
        const next = { ...item, loadingFrls: action.enabled && item.supportsFrls };
        return repriceIfCalculated(next);
      });
    case "toggle-pvc":
      return updateCartItemWithReprice(state, action.id, (item) => {
        const next = { ...item, loadingPvc: action.enabled && item.supportsPvc };
        return repriceIfCalculated(next);
      });
    case "set-comparison-brand":
      return {
        ...state,
        comparison: {
          brand: action.brand,
          rows: []
        }
      };
    case "sync-comparison-items":
      {
        const rows = state.comparison.rows.filter((row) =>
          action.itemIds.includes(row.cartItemId)
        );
        if (rows.length === state.comparison.rows.length) {
          return state;
        }

        return {
          ...state,
          comparison: {
            ...state.comparison,
            rows
          }
        };
      }
    case "update-comparison-discount": {
      const discount =
        action.discount === undefined ? undefined : normalizePercent(action.discount);
      return updateComparisonRow(state, action.id, {
        discount,
        pricingMode: "discount",
        warning: undefined
      });
    }
    case "update-comparison-rate":
      return updateComparisonRow(state, action.id, {
        rate: action.rate === undefined ? undefined : normalizeNonNegativeNumber(action.rate),
        pricingMode: "manual",
        warning: undefined
      });
    case "set-comparison-list-price":
      if (state.comparison.brand !== action.brand) {
        return state;
      }

      return updateComparisonRow(state, action.id, {
        listPrice: action.listPrice,
        pricingMode: "discount",
        warning: action.warning
      });
    case "sync-selection":
      if (
        state.selectedBrand === action.brand &&
        state.selectedCategory === action.category &&
        state.selectedPricelistTag === action.tag
      ) {
        return state;
      }

      return {
        ...state,
        selectedBrand: action.brand,
        selectedCategory: action.category,
        selectedPricelistTag: action.tag
      };
  }
}

function addCellToCart(
  state: WorkspaceState,
  grid: PricelistGridDto,
  cell: PricelistGridCellDto
): WorkspaceState {
  const id = makeCartItemId(cell, grid.tag);
  const existing = state.cart.find((item) => item.id === id);
  if (existing) {
    return updateCartItem(state, id, { quantity: (existing.quantity ?? 0) + 1 });
  }

  const item: CartItem = {
    id,
    brand: grid.brand,
    category: grid.category,
    pricelistDisplayName: grid.display_name,
    tag: grid.tag,
    product: cell.product,
    description: cell.label,
    listPrice: cell.price,
    quantity: undefined,
    discount: state.globalDiscount,
    rate: calculateRateFromDiscount(cell.price, state.globalDiscount ?? 0, false, false),
    markup: state.globalMarkup,
    loadingFrls: false,
    loadingPvc: false,
    supportsFrls: cell.supports_frls,
    supportsPvc: cell.supports_pvc,
    pricingMode: "global"
  };

  return { ...state, cart: [...state.cart, item] };
}

function updateCartItem(
  state: WorkspaceState,
  id: string,
  patch: Partial<CartItem>
): WorkspaceState {
  return {
    ...state,
    cart: state.cart.map((item) => (item.id === id ? { ...item, ...patch } : item))
  };
}

function updateCartItemWithReprice(
  state: WorkspaceState,
  id: string,
  updater: (item: CartItem) => CartItem
): WorkspaceState {
  return {
    ...state,
    cart: state.cart.map((item) => (item.id === id ? updater(item) : item))
  };
}

function updateComparisonRow(
  state: WorkspaceState,
  cartItemId: string,
  patch: Partial<WorkspaceState["comparison"]["rows"][number]>
): WorkspaceState {
  const existingRow = state.comparison.rows.find((row) => row.cartItemId === cartItemId);
  const nextRow = {
    cartItemId,
    listPrice: undefined,
    discount: undefined,
    rate: undefined,
    pricingMode: "discount" as const,
    warning: undefined,
    ...existingRow,
    ...patch
  };

  return {
    ...state,
    comparison: {
      ...state.comparison,
      rows: existingRow
        ? state.comparison.rows.map((row) =>
            row.cartItemId === cartItemId ? nextRow : row
          )
        : [...state.comparison.rows, nextRow]
    }
  };
}

function repriceIfCalculated(item: CartItem): CartItem {
  if (item.pricingMode === "manual") {
    return item;
  }

  return { ...item, pricingMode: item.pricingMode ?? "global", rate: rateForItem(item, item.discount ?? 0) };
}

function followsGlobalPricing(item: CartItem): boolean {
  return item.pricingMode === "global" || item.pricingMode === undefined;
}

function rateForItem(item: CartItem, discount: number): number {
  return calculateRateFromDiscount(
    item.listPrice,
    discount,
    item.loadingFrls,
    item.loadingPvc
  );
}
